import {
  AppBar,
  Card,
  DialogContent,
  DialogContentText,
  Button,
  Paper,
  Toolbar,
  Typography,
  makeStyles,
} from '@material-ui/core';
import StakingService, { StakingMode } from '../../services/stakingService';
import { useIsExtensionWidth } from '../../utils/utils';
import { NewDelegateStake } from '../staking/surfaces/NewDelegateStake';
import { PurchaseCard } from '../staking/surfaces/PurchaseCard';
import { STAKING_BACKEND_URL } from '../../utils/env-variables';
import useLocalStorageState from 'use-local-storage-state';
import { useQuery } from '@tanstack/react-query';
import { useState, useEffect, useRef } from 'react';
import { useWallet } from '../../utils/wallet';
import { PublicKey, ParsedAccountData } from '@solana/web3.js';
import { match, P } from 'ts-pattern';
import LoadingIndicator from '../LoadingIndicator';
import { useConnection } from '../../utils/connection';
import NewActiveStakeInfo from '../staking/surfaces/NewActiveStakeInfo';
import AINodeActiveStakeInfo from '../staking/surfaces/AIStakeInfo';
import ActiveStakeInfo from '../staking/surfaces/ClassicActiveStakeInfo'


const useStyles = makeStyles((theme) => ({
  card: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    height: "100%",
    width: "100%",
    padding: theme.spacing(3),
    backgroundColor: theme.palette.background.default,
  },
}));

const STAKING_MODE: StakingMode = 'classic';
const STAKE_PROGRAM_ID = 'Stake11111111111111111111111111111111111111';
const STAKE_LOCKUP_CUSTODIAN = '11111111111111111111111111111111';

const M0_OWNERSHIP_KEY = "m0MachineOwnedWallets";
const M0_TIMESTAMP_KEY = "m0MachineCheckTimestamp";
const CACHE_EXPIRY = 24 * 60 * 60 * 1000; // 24 hours

const isParsedAccountData = (data: Buffer | ParsedAccountData): data is ParsedAccountData => {
  return (data as ParsedAccountData).parsed !== undefined;
};

export default function StakingForm() {
  const classes = useStyles();
  const isExtensionWidth = useIsExtensionWidth();
  const wallet = useWallet();
  const connection = useConnection();

  const [isM0Owner, setIsM0Owner] = useState(false);
  const [isCheckingM0, setIsCheckingM0] = useState(false);
  const isMounted = useRef(true);

  const [stakingBackendUrl] = useLocalStorageState('stackingBackendUrl', {
    defaultValue: STAKING_BACKEND_URL,
  });

  const stakingMode = STAKING_MODE;
  const oldStakeMode: StakingMode = 'new';

  const stakingService = new StakingService(wallet, connection,
    stakingBackendUrl, stakingMode);

  const stakingServiceClone = new StakingService(wallet, connection,
    stakingBackendUrl, oldStakeMode);

  const walletKey = wallet.publicKey?.toBase58();

  const { data: stakeAccount } = useQuery<PublicKey>({
    queryKey: ['stakeAccount', walletKey, stakingMode],
    queryFn: () => stakingService.getStakeAccount(),
    enabled: Boolean(walletKey),
  });

  const { data: oldStakeAccount } = useQuery<PublicKey>({
    queryKey: ['stakeAccount', walletKey, oldStakeMode],
    queryFn: () => stakingServiceClone.getStakeAccount(),
    enabled: Boolean(walletKey),
  });

  const { data: aiNodeStakeAccount } = useQuery<PublicKey>({
    queryKey: ['aiNodeStakeAccount', walletKey, 'miner'],
    queryFn: () => stakingService.getAINodeStakeAccount(),
    enabled: Boolean(walletKey),
  });

  const { data: aiNodeStakeAccountInfo, isFetched: aiNodeActivationFetched } = useQuery({
    queryKey: ['aiNodeStakeAccountInfo', aiNodeStakeAccount?.toBase58()],
    queryFn: async () => {
      try {
        return await connection.getStakeActivation(aiNodeStakeAccount as PublicKey);
      } catch {
        return null;
      }
    },
    enabled: Boolean(aiNodeStakeAccount),
    refetchInterval: 30 * 1000,
  });
  
  // Check for AI Node certification
  const { data: aiNodeCertificate } = useQuery({
    queryKey: ['aiNodeCertificate', wallet.publicKey?.toBase58()],
    queryFn: async () => {
      try {
        return await stakingService.getAINodeCertificate();
      } catch (error) {
        console.error('Failed to get AI Node certificate:', error);
        return null;
      }
    },
    enabled: Boolean(wallet.publicKey),
  });

  // Check for M0
  const getM0Ownership = (walletAddress) => {
    try {
      const stored = localStorage.getItem(M0_OWNERSHIP_KEY);
      if (stored === null) return null;
      
      let ownedWallets;
      try {
        ownedWallets = JSON.parse(stored);
      } catch (e) {
        localStorage.removeItem(M0_OWNERSHIP_KEY);
        return null;
      }
      
      if (typeof ownedWallets !== 'object' || ownedWallets === null) {
        localStorage.removeItem(M0_OWNERSHIP_KEY);
        return null;
      }
      
      return ownedWallets[walletAddress] || null;
    } catch (e) {
      return null;
    }
  };

  const isCacheExpired = (walletAddress) => {
    try {
      const timestamp = localStorage.getItem(M0_TIMESTAMP_KEY);
      if (!timestamp) return true;
      
      let timestamps;
      try {
        timestamps = JSON.parse(timestamp);
      } catch (e) {
        localStorage.removeItem(M0_TIMESTAMP_KEY);
        return true;
      }
      
      if (typeof timestamps !== 'object' || timestamps === null) {
        localStorage.removeItem(M0_TIMESTAMP_KEY);
        return true;
      }
      
      const storedTime = timestamps[walletAddress];
      
      if (!storedTime) return true;
      
      const now = Date.now();
      return (now - storedTime) > CACHE_EXPIRY;
    } catch (e) {
      return true;
    }
  };

  useEffect(() => {
    // Cleanup function to prevent memory leaks
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (!wallet?.publicKey) return;
    
    const walletAddress = wallet.publicKey.toString();
    const cachedOwnership = getM0Ownership(walletAddress);
    
    // If we have a cached result and it's not expired, use it
    if (cachedOwnership !== null && !isCacheExpired(walletAddress)) {
      setIsM0Owner(cachedOwnership);
      return;
    }
    
    async function checkM0Ownership() {
      if (!isMounted.current) return;
      
      try {
        setIsCheckingM0(true);
    
        const response = await fetch(`https://airdrop2.domichain.io/v1/miners/m0wallet?walletAddress=${walletAddress}`);
    
        if (!response.ok) {
          const errorText = await response.text();
          console.error("Error checking M0 ownership:", response.status, errorText);
          return;
        }
    
        let data;
        try {
          data = await response.json();
        } catch (jsonError) {
          const rawText = await response.text();
          console.error("Failed to parse JSON from response:", jsonError, "Raw response:", rawText);
          return;
        }
        
        if (!isMounted.current) return;
        
        const m0Data = data.data;
    
        if (m0Data && m0Data.success && typeof m0Data.hasM0 !== 'undefined') {
          setIsM0Owner(m0Data.hasM0);
          
          const ownedWallets = JSON.parse(localStorage.getItem(M0_OWNERSHIP_KEY) || '{}');
          ownedWallets[walletAddress] = m0Data.hasM0;
          localStorage.setItem(M0_OWNERSHIP_KEY, JSON.stringify(ownedWallets));
          
          const timestamps = JSON.parse(localStorage.getItem(M0_TIMESTAMP_KEY) || '{}');
          timestamps[walletAddress] = Date.now();
          localStorage.setItem(M0_TIMESTAMP_KEY, JSON.stringify(timestamps));
        } else {
          console.error("Unexpected M0 ownership response format:", m0Data);
        }
      } catch (error) {
        console.error("Failed to check M0 ownership:", error || "Unknown error");
      } finally {
        if (isMounted.current) {
          setIsCheckingM0(false);
        }
      }
    }
    
    checkM0Ownership();
  }, [wallet?.publicKey]);

  // Get AI Node staking plan
  const { data: aiNodeStakingPlan } = useQuery({
    queryKey: ['aiNodeStakingPlan', stakingBackendUrl],
    queryFn: async () => {
      return await stakingService.fetchAINodeStakePlan();
    },
    enabled: Boolean(aiNodeCertificate),
    retry: 5,
    retryDelay: 5000,
  });

  const hasAINode = Boolean(aiNodeCertificate);
  const isMinerFetched = aiNodeActivationFetched;
  const aiNodeStakingPlanLoaded = !!aiNodeStakingPlan;
  let aiPlan;

  if (aiNodeStakingPlan) {
    aiPlan = aiNodeStakingPlan.minerType;
  }

  // Get stake activation state
  const { data: stakeAccountInfo, isFetched: activationFetched } = useQuery({
    queryKey: ['stakeAccountInfo', stakeAccount?.toBase58()],
    queryFn: async () => {
      try {
        return await connection.getStakeActivation(stakeAccount as PublicKey);
      } catch {
        return null;
      }
    },
    enabled: Boolean(stakeAccount),
    refetchInterval: 30 * 1000,
  });

  // Get detailed account info 
  const { data: detailedAccountInfo, isFetched: detailsFetched } = useQuery({
    queryKey: ['detailedAccountInfo', stakeAccount?.toBase58()],
    queryFn: async () => {
      if (!stakeAccount) return null;
      try {
        const accountInfo = await connection.getParsedAccountInfo(stakeAccount);
        if (!accountInfo.value) return null;

        const data = accountInfo.value.data as ParsedAccountData;
        if (!data.parsed) return null;

        return {
          parsed: data.parsed,
          owner: accountInfo.value.owner.toBase58()
        };
      } catch (error) {
        console.error('Error fetching detailed account info:', error);
        return null;
      }
    },
    enabled: Boolean(stakeAccount),
  });

  // Get old stake activation state
  const { data: oldStakeAccountInfo, isFetched: oldActivationFetched } = useQuery({
    queryKey: ['stakeAccountInfo', oldStakeAccount?.toBase58()],
    queryFn: async () => {
      try {
        return await connection.getStakeActivation(oldStakeAccount as PublicKey);
      } catch {
        return null;
      }
    },
    enabled: Boolean(oldStakeAccount),
    refetchInterval: 30 * 1000,
  });

  const { data: stakingPlan } = useQuery({
    queryKey: ['stakingPlan', stakingBackendUrl],
    queryFn: async () => {
      return await stakingService.fetchNewStakePlan();
    },
    retry: 5,
    retryDelay: 5000,
  });

  const isFetched = activationFetched && detailsFetched;

  // Check if account is initialized but not delegated
  const isInitializedNotDelegated = detailedAccountInfo && 
    detailedAccountInfo.owner === STAKE_PROGRAM_ID &&
    detailedAccountInfo.parsed.type === "initialized" &&
    detailedAccountInfo.parsed.info.meta && 
    detailedAccountInfo.parsed.info.meta.authorized &&
    detailedAccountInfo.parsed.info.meta.authorized.staker === wallet.publicKey?.toBase58() &&
    detailedAccountInfo.parsed.info.meta.lockup &&
    detailedAccountInfo.parsed.info.meta.lockup.custodian === STAKE_LOCKUP_CUSTODIAN &&
    !detailedAccountInfo.parsed.info.stake;

  /*   // Debug logging
  console.log('Stake Account Details:', {
    stakeAccount: stakeAccount?.toBase58(),
    owner: detailedAccountInfo?.owner,
    isProgramMatch: detailedAccountInfo?.owner === STAKE_PROGRAM_ID,
    isInitialized: detailedAccountInfo?.parsed.type === "initialized",
    hasMeta: detailedAccountInfo?.parsed.info.meta,
    staker: detailedAccountInfo?.parsed.info.meta?.authorized?.staker,
    isStakerMatch: detailedAccountInfo?.parsed.info.meta?.authorized?.staker === wallet.publicKey?.toBase58(),
    lockupCustodian: detailedAccountInfo?.parsed.info.meta?.lockup?.custodian,
    hasStake: detailedAccountInfo?.parsed.info.stake,
    isInitializedNotDelegated
  });
    */

  const stakingPlanLoaded = !!stakingPlan;

  // Determine whether to show classic ActiveStakeInfo
  const showOldStakeInfo = Boolean(oldStakeAccount && oldStakeAccountInfo) && !isInitializedNotDelegated;

  return (
    <Paper>
      <AppBar position="static" color="default" elevation={1}>
        <Toolbar>
          <Typography
            variant="h6"
            style={{ flexGrow: 1, fontSize: isExtensionWidth && '1rem' || undefined }}
            component="h2"
          >
            {"Staking"}
          </Typography>
        </Toolbar>
      </AppBar>
      <Card className={classes.card}>
        {!isFetched || !stakingPlanLoaded ? (
          <DialogContent>
            <LoadingIndicator delay={0} />
          </DialogContent>
        ) : match([stakeAccountInfo, isInitializedNotDelegated])
          .with([P.not(P.nullish), false], () => (
            <NewActiveStakeInfo
              stakeAccount={stakeAccount!}
              stakeAccountInfo={stakeAccountInfo!}
              stakingBackendUrl={stakingBackendUrl!}
              stakingMode={stakingMode}
            />
          ))
          .with([P.any, true], () => (
            <NewDelegateStake
              stakingPlan={stakingPlan!}
              stakingBackendUrl={stakingBackendUrl}
              stakingMode={stakingMode}
              isInitializedNotDelegated={true}
              hasAINode={hasAINode || isM0Owner}
              plan={aiPlan} 
            />
          ))
          .otherwise(() => (
            <NewDelegateStake
              stakingPlan={stakingPlan!}
              stakingBackendUrl={stakingBackendUrl}
              stakingMode={stakingMode}
              isInitializedNotDelegated={false}
              hasAINode={hasAINode || isM0Owner}
              plan={aiPlan} 
            />
          ))}
      </Card>

      {showOldStakeInfo && (
        <Card className={classes.card} style={{ marginTop: '20px' }}>
          <ActiveStakeInfo
            stakeAccount={oldStakeAccount!}
            stakeAccountInfo={oldStakeAccountInfo!}
            stakingBackendUrl={stakingBackendUrl!}
            stakingMode={oldStakeMode}
          />
        </Card>
      )}
      
      {hasAINode && (
        <Card className={classes.card} style={{ marginTop: '20px' }}>
          <AppBar position="static" color="default" elevation={1}>
            <Toolbar>
              <Typography
                variant="h6"
                style={{ flexGrow: 1, fontSize: isExtensionWidth && '1rem' || undefined }}
                component="h2"
              >
                {"AI Node Staking"}
              </Typography>
            </Toolbar>
          </AppBar>
          
          {!isMinerFetched || !aiNodeStakingPlanLoaded ? (
            <DialogContent>
              <LoadingIndicator delay={0} />
            </DialogContent>
          ) : aiNodeStakeAccountInfo ? (
            <AINodeActiveStakeInfo
              stakeAccount={aiNodeStakeAccount!}
              stakeAccountInfo={aiNodeStakeAccountInfo}
              stakingBackendUrl={stakingBackendUrl!}
              stakingMode="miner"
            />
          ) : null}
        </Card>
      )}
    </Paper>
  );
}